import * as React from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../theming/fontSizeTranslator';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { maxVisibleLinksOnLargeAndXLarge } from '../../components/navigation/header/secondaryHeader/SecondaryHeader';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../partials/link/TextLink';
import { isLastLink } from '../../components/navigation/header/secondaryHeader/SecondaryLink';
import { getStyleBySize, semanticCSS } from '../../../typography/semanticTags/semanticTags.utils';
import { Span } from '../../../typography/semanticTags';
import { useTheme } from '../../../theming/useTheme';

const componentConfigThemeKey = 'secondaryLink';

interface AMPSecondaryLinkDataProps {
  link: HeaderLinksDataProps;
  index: number;
  linksLength: number;
  pagePath: string;
}

interface AMPSecondaryLinkThemeProps {
  idleColor: string;
  activeStateColor: string;
  hoverStateColor: string;
  baseUnit: BaseUnit;
  fontSizesStyles: TranslatedFontSize;
}

const getTextStyle = (baseUnit: BaseUnit, isActiveLink: boolean, lastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      fontWeight: isActiveLink ? 600 : 400,
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: lastLink ? `${baseUnit * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: lastLink ? `${baseUnit * 2}px` : 0,
      },
    },
  });
};

export const AMPSecondaryLink: React.FunctionComponent<AMPSecondaryLinkDataProps> = props => {
  const { link, pagePath, linksLength, index } = props;
  const { activeStateColor, idleColor, hoverStateColor, baseUnit, fontSizesStyles } = useTheme<AMPSecondaryLinkThemeProps>(componentConfigThemeKey);
  const { href, text } = link;
  const isActiveLink = href === pagePath;

  const linkStyleBasedOnScreen = linksLength > maxVisibleLinksOnLargeAndXLarge && index >= maxVisibleLinksOnLargeAndXLarge - 1
    ? {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex',
      },
    }
    : {};
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: isActiveLink ? activeStateColor : idleColor,
    activeStateColor,
    hoverStateColor: isActiveLink ? activeStateColor : hoverStateColor,
  }, linkStyleBasedOnScreen);
  const style = getTextStyle(baseUnit, isActiveLink, isLastLink(index, linksLength));
  const fontsSizesForActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
  const fontsSizesForNotActive = { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL };
  const contentStyle = getStyleBySize(fontSizesStyles, isActiveLink ? fontsSizesForActive : fontsSizesForNotActive);
  return (
    <TextLinkComponent href={href} linkStyle={linkStyle}>
      <Span styles={semanticCSS(contentStyle, style.link)}>{text}</Span>
    </TextLinkComponent>
  );
};
